import { useEffect, useMemo } from 'react'
import { Hex, formatEther } from 'viem'
import { toast } from 'react-toastify'
import { cn } from '@/lib/utils'
import { useUserSubmittedAnswer } from '@/hooks/survey4-contract/useSurvey4UserSubmittedAnswer'
import { useWriteSurvey4Contract } from '@/hooks/survey4-contract/useWriteSurvey4Contract'
import { useSurvey4Answers } from '@/hooks/survey4-contract/useSurvey4ChainData'
import { useCurrencyFormatter } from '@/hooks/helpers/useCurrencyFormatter'
import { useStaking2aUserStakeData } from '@/hooks/staking2a-contract/useStaking2aUserStakeData'
import { usePepperBalance } from '@/hooks/pepper-contract/usePepperBalance'
import Info from '@/assets/icons/Info.svg?react'
import { PollCard, type PollProps } from '@/components/poll/PollCard'
import { Button } from '@/components/ui/Button'
import { waitForTransactionReceipt } from '@/utils/wagmi/waitForTransactionReceipt'
import { getPollOptions } from '@/utils/polls/getPollsOptions'
import { VoteDialog } from '../dialogs/VoteDialog'
import { StakeAndVoteDialog } from '../dialogs/StakeAndVoteDialog'
import { waitForNextMinedBlocks } from '@/utils/wagmi/wait-for-block'
import { logger } from '@/observability/logger'
import { Skeleton } from '@/components/ui/Skeleton'
import { motion, AnimatePresence } from 'framer-motion'
import { useTimeout } from '@/hooks/helpers/useTimeout'
import React from 'react'
import { useResponsiveDialog } from '@/components/ui/ResponsiveDialog'
import { formatDateTime } from '@/utils/datetime/formatDate'
import { CalendarDays } from 'lucide-react'
import { PollCountdown } from '@/components/poll/PollCountDown'
import type { SurveyDTOStatus } from '@/data-access/surveys/types'
import { testIdFactory } from '@/utils/test-id-factory.ts'

interface UserPollProps extends Omit<PollProps, 'options'> {
	address: Hex
	votingCap: bigint
	userAddress?: Hex
	lockedPepper?: bigint
	loading?: boolean
	userUnlockedTokens?: bigint
	userLockedTokens?: bigint
	isConnected?: boolean
	status: SurveyDTOStatus
	onEnded?: (surveyAddress: Promise<Hex>) => void
	testIdDiscriminator: string
}

function _UserPollCard({
	address,
	startDate,
	endDate,
	lockedPepper,
	loading,
	className,
	isConnected,
	userAddress,
	userUnlockedTokens = 0n,
	userLockedTokens = 0n,
	votingCap,
	status,
	onEnded,
	testIdDiscriminator,
	...props
}: UserPollProps) {
	// Hooks
	const writeSurvey4ChainData = useWriteSurvey4Contract(address)
	const {
		userSubmittedAnswer,
		userSubmittedAnswerWeight,
		isPending: isUserAnswerPending,
		refetch: refetchUserAnswer
	} = useUserSubmittedAnswer(address)
	const format = useCurrencyFormatter()
	const [voteDialogProps, openVoteDialog, closeVoteDialog] = useResponsiveDialog({ resetOnClose: true })
	const [stakeAndVoteDialogProps, openStakeAndVoteDialog, closeStakeAndVoteDialog] = useResponsiveDialog({
		resetOnClose: true
	})
	const { refetch: refetchUserStakedData } = useStaking2aUserStakeData()
	const { refetch: refetchPepperBalance } = usePepperBalance()

	// Rely on the chain for the latest data
	const {
		data: answers = [],
		refetch: refetchAnswers,
		isPending: isSurvey4AnswersPending
	} = useSurvey4Answers(address)

	const options = useMemo(() => getPollOptions([...answers]), [answers])

	const isLoading = isSurvey4AnswersPending || isUserAnswerPending

	const upcomingPoll = status === 'upcoming'
	const ongoingPoll = status === 'ongoing'
	const endedPoll = status === 'ended'

	const handleRefetch = () =>
		Promise.all([refetchUserStakedData(), refetchPepperBalance(), refetchUserAnswer(), refetchAnswers()])

	const onCloseVoteDialog = () => Promise.all([handleRefetch(), closeVoteDialog()])

	const onCloseStakeAndVoteDialog = () =>
		Promise.all([refetchPepperBalance(), handleRefetch(), closeStakeAndVoteDialog()])

	/*
	 * useEffect for refetching data when userAddress changes
	 * TODO(DFT-1951): Refactor this to avoid using the useEffect
	 * and include the userAddress as part of the query key so we can do it in the definition of the hook
	 * (ex: useReadContract), so the cache is invalidated on address change
	 * https://gitlab.mediarex.com/mediarex/blockchain/pepperchain-ui/-/merge_requests/165#note_511836
	 */
	useEffect(() => {
		if (userAddress) {
			handleRefetch()
		}
	}, [userAddress])

	useTimeout(
		() => {
			const addressPromise = new Promise<Hex>(resolve => {
				waitForNextMinedBlocks(2).then(() => {
					handleRefetch()
					resolve(address)
				})
			})

			onEnded?.(addressPromise)
		},
		ongoingPoll && endDate ? endDate.getTime() - Date.now() : 0,
		!ongoingPoll
	)

	const onVote = async (answer: string, amountToLock: bigint) => {
		try {
			const hash = await writeSurvey4ChainData({
				functionName: 'vote',
				args: [answer, amountToLock]
			})
			await waitForTransactionReceipt({ hash })
			await handleRefetch()
			return hash
		} catch (e) {
			logger.error(e)
			toast.error('An error occurred while voting')
		}
	}

	const availableTokensForVoting = userUnlockedTokens + userLockedTokens

	const handleClick =
		ongoingPoll && isConnected && availableTokensForVoting > 0n ? openVoteDialog : openStakeAndVoteDialog

	return (
		<AnimatePresence mode="wait">
			{isLoading ? (
				<motion.div
					key="skeleton"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.3 }}
					className={cn('w-full h-[600px]', className)}
				>
					<Skeleton className="size-full" />
				</motion.div>
			) : (
				<motion.div
					key="content"
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
					transition={{ duration: 0.3 }}
					className={cn('p-1 pt-0 rounded-4xl', className)}
				>
					{(ongoingPoll || upcomingPoll) && (
						<p
							className={cn('leading-none p-3 text-neutral flex items-center justify-center gap-1', {
								'text-neutral-40': upcomingPoll
							})}
						>
							<span className="text-2xl leading-none">
								{ongoingPoll ? 'Vote period ends in:' : 'Vote period starts in:'}
							</span>
							<PollCountdown endDate={ongoingPoll ? endDate : startDate} />
						</p>
					)}

					<PollCard
						startDate={startDate}
						endDate={endDate}
						inactive={!isConnected || !!userSubmittedAnswer}
						selectedOption={userSubmittedAnswer}
						options={options}
						testIdDiscriminator={testIdDiscriminator}
						{...props}
					>
						{({ selectedOption, isDisabled, isReadonly }) => (
							<div className="flex flex-col items-center gap-5">
								{endedPoll ? (
									<div className="flex items-center gap-2">
										<CalendarDays size={24} className="text-neutral-40" />
										<p className="text-2xl text-bold text-neutral-40">
											Vote ended on {formatDateTime(endDate.getTime(), false)}
										</p>
									</div>
								) : (
									<Button
										variant="danger"
										className="py-5 px-10 min-w-[215px]"
										loading={loading}
										disabled={
											upcomingPoll ||
											!selectedOption ||
											isReadonly ||
											!ongoingPoll ||
											isDisabled ||
											!!userSubmittedAnswer ||
											!isConnected
										}
										data-testid={
											ongoingPoll && isConnected && !userSubmittedAnswer
												? availableTokensForVoting > 0n
													? testIdFactory.pepperVoteButton(testIdDiscriminator)
													: testIdFactory.pepperStakeAndVoteButton(testIdDiscriminator)
												: null
										}
										onClick={handleClick}
									>
										{upcomingPoll
											? 'Vote unavailable'
											: ongoingPoll && isConnected && userSubmittedAnswer
												? 'Vote submitted'
												: ongoingPoll
													? isConnected && availableTokensForVoting > 0n
														? 'Vote'
														: 'Stake & Vote'
													: 'Vote has ended'}
									</Button>
								)}
								{ongoingPoll && (
									<div className="flex gap-2 items-start md:items-center text-neutral-30">
										<Info className="w-[2.5rem] md:w-[1.5rem]" />
										<span className="text-2xl leading-none">
											{userSubmittedAnswer
												? `You currently have ${format(formatEther(userSubmittedAnswerWeight ?? 0n))} PEPPER locked for this poll.`
												: isReadonly && isConnected
													? `You currently have ${format(lockedPepper ?? 0n)} PEPPER locked for this poll.`
													: 'PEPPER staked are locked for the duration of the vote.'}
										</span>
									</div>
								)}
								{onVote && selectedOption && (
									<VoteDialog
										selectedOption={selectedOption}
										userUnlockedTokens={userUnlockedTokens}
										userLockedTokens={userLockedTokens}
										onVote={onVote}
										votingCap={votingCap}
										onSubmit={onCloseVoteDialog}
										{...voteDialogProps}
										key={voteDialogProps.key}
									/>
								)}
								{onVote && selectedOption && (
									<StakeAndVoteDialog
										selectedOption={selectedOption}
										userUnlockedTokens={userUnlockedTokens}
										votingCap={votingCap}
										onVote={onVote}
										onSubmit={onCloseStakeAndVoteDialog}
										{...stakeAndVoteDialogProps}
										key={stakeAndVoteDialogProps.key}
									/>
								)}
							</div>
						)}
					</PollCard>
				</motion.div>
			)}
		</AnimatePresence>
	)
}

export const UserPollCard = React.memo(_UserPollCard)
